import React from "react";
import { useFeatureFlag, useFeatureFlags, useAllFeatureFlags } from "@/hooks/useFeatureFlag";
import { Skeleton } from "@/components/ui/skeleton";
import { UpgradeDialog } from "./UpgradeDialog";
import { Plan } from "@deepguide-ai/types";

interface FeatureGateProps {
  flag: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoader?: boolean;
  showUpgradePrompt?: boolean;
}

/**
 * Component to conditionally render content based on feature flag status
 *
 * Use this to hide/show entire sections based on feature flags.
 * If showUpgradePrompt is true, shows upgrade dialog instead of fallback.
 */
export function FeatureGate({
  flag,
  children,
  fallback = null,
  showLoader = false,
  showUpgradePrompt = false,
}: FeatureGateProps) {
  const { enabled, loading, reason, metadata } = useFeatureFlag(flag);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = React.useState(false);

  if (loading && showLoader) {
    return <Skeleton className="h-20 w-full" />;
  }

  if (!enabled) {
    if (showUpgradePrompt && reason === 'plan_not_allowed') {
      return (
        <>
          <div
            onClick={() => setUpgradeDialogOpen(true)}
            className="cursor-pointer"
          >
            {fallback}
          </div>
          <UpgradeDialog
            open={upgradeDialogOpen}
            onOpenChange={setUpgradeDialogOpen}
            featureName={`Feature: ${flag}`}
            featureDescription="This feature is available on higher plans"
            requiredPlan={metadata?.requiredPlan as Plan}
            currentPlan={metadata?.currentPlan as Plan}
          />
        </>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface MultiFeatureGateProps {
  flags: string[];
  mode?: "all" | "any";
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoader?: boolean;
}

/**
 * Component to conditionally render content based on multiple feature flags
 */
export function MultiFeatureGate({
  flags,
  mode = "all",
  children,
  fallback = null,
  showLoader = false,
}: MultiFeatureGateProps) {
  const { flags: allFlags, loading } = useFeatureFlags(flags);
  
  const enabled = mode === "all" 
    ? flags.every(flag => allFlags[flag])
    : flags.some(flag => allFlags[flag]);
  
  if (loading && showLoader) {
    return <Skeleton className="h-20 w-full" />;
  }
  
  if (!enabled) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

interface PlanGateProps {
  requiredPlan: Plan;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
  featureName?: string;
  featureDescription?: string;
}

/**
 * Component to conditionally render content based on subscription plan
 *
 * Use this to hide/show entire sections based on plan level.
 */
export function PlanGate({
  requiredPlan,
  children,
  fallback,
  showUpgradePrompt = true,
  featureName = "This feature",
  featureDescription = "This feature is available on higher plans",
}: PlanGateProps) {
  const { plan, loading } = useAllFeatureFlags();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = React.useState(false);

  if (loading) {
    return <Skeleton className="h-20 w-full" />;
  }

  const planHierarchy: Partial<Record<Plan, number>> = {
    FREE: 0,
    PRO: 3,
    ENTERPRISE: 4,
  };

  const currentPlanLevel = planHierarchy[plan] || 0;
  const requiredPlanLevel = planHierarchy[requiredPlan] || 0;
  const hasAccess = currentPlanLevel >= requiredPlanLevel;

  if (!hasAccess) {
    if (showUpgradePrompt) {
      return (
        <>
          <div
            onClick={() => setUpgradeDialogOpen(true)}
            className="cursor-pointer"
          >
            {fallback || (
              <div className="text-muted-foreground text-sm">
                {featureName} requires {requiredPlan} plan
              </div>
            )}
          </div>
          <UpgradeDialog
            open={upgradeDialogOpen}
            onOpenChange={setUpgradeDialogOpen}
            featureName={featureName}
            featureDescription={featureDescription}
            requiredPlan={requiredPlan}
            currentPlan={plan}
          />
        </>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
}